import React from "react";
import {
  Laptop, Wifi, Smartphone, Video, Printer, Mail,
  HardDrive, ShieldCheck, MonitorSmartphone, BadgeCheck
} from "lucide-react";

const SERVICES = [
  {
    icon: BadgeCheck,
    title: "myGov & myID Setup",
    desc: "Set up myGov, myID (formerly myGovID), Medicare & Centrelink linking. I’ll walk you through it and sort out lock-outs or verification issues.",
    highlight: true,
  },
  {
    icon: Laptop,
    title: "Computer & Laptop Help",
    desc: "Slow computer? Pop-ups? Forgotten password? I sort it out and explain it in plain English.",
  },
  {
    icon: Wifi,
    title: "Wi-Fi & Internet",
    desc: "Set up your home internet, fix dropouts, or get a new modem talking to your devices.",
  },
  {
    icon: Smartphone,
    title: "Phones & Tablets",
    desc: "iPhone, iPad, Android — I’ll help you with messages, photos, apps, and updates.",
  },
  {
    icon: Video,
    title: "Video Calls with Family",
    desc: "FaceTime, WhatsApp, Zoom — set it up so you can see the grandkids any time.",
  },
  {
    icon: Printer,
    title: "Printers & Smart TVs",
    desc: "Get your printer working again, or set up Netflix and catch-up TV on your telly.",
  },
  {
    icon: Mail,
    title: "Email & Online Accounts",
    desc: "Sorting out email, banking logins, or signing up for things — calmly, step-by-step.",
  },
  {
    icon: HardDrive,
    title: "Backups & Photo Rescue",
    desc: "Keep your precious photos safe with a simple backup. Recover deleted files where possible.",
  },
  {
    icon: ShieldCheck,
    title: "Scam & Safety Check-ups",
    desc: "Worried something dodgy is going on? I’ll check your devices and show you the warning signs.",
  },
  {
    icon: MonitorSmartphone,
    title: "Remote Help by Phone",
    desc: "Can’t wait? I can guide you over the phone or jump in remotely (with your permission).",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="pt-8 md:pt-12 pb-20 md:pb-24 bg-[#FFFFFF]">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl">
          <p className="text-base md:text-lg font-semibold text-[#6BAF3C] uppercase tracking-wide">What I can help with</p>
          <h2 className="mt-3 font-[Work_Sans] text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F2E52]">
            Simple, friendly help with anything tech.
          </h2>
          <p className="mt-4 text-lg md:text-xl text-[#1E3A5F]">
            If it has a screen, a cable, or a Wi-Fi signal, I can probably help.
            And if I can’t, I’ll tell you honestly and point you in the right direction.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`service-card-${idx}`}
                className={
                  s.highlight
                    ? "relative bg-gradient-to-br from-[#F0F7E8] to-white border-2 border-[#6BAF3C] rounded-2xl p-6 md:p-7 shadow-lg shadow-[#6BAF3C]/10 transition-colors"
                    : "bg-white border border-[#DCE4EC] rounded-2xl p-6 md:p-7 hover:border-[#0F2E52] transition-colors"
                }
              >
                {s.highlight && (
                  <span className="absolute -top-3 left-4 bg-[#6BAF3C] text-white text-[10px] md:text-xs font-[Work_Sans] font-black uppercase tracking-widest rounded-full px-3 py-1 shadow-sm">
                    Popular with seniors
                  </span>
                )}
                <div className={`w-14 h-14 rounded-xl grid place-items-center mb-5 ${s.highlight ? "bg-[#6BAF3C]" : "bg-[#F0F7E8]"}`}>
                  <Icon className={`w-7 h-7 ${s.highlight ? "text-white" : "text-[#0F2E52]"}`} strokeWidth={2.2} />
                </div>
                <h3 className="font-[Work_Sans] text-xl md:text-2xl font-semibold text-[#0F2E52]">
                  {s.title}
                </h3>
                <p className="mt-3 text-base md:text-lg text-[#1E3A5F] leading-relaxed">
                  {s.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
